'use client';

import {
  DataGridPro,
  GridColDef,
  GridColumnVisibilityModel,
  GridDensity,
  GridRowSelectionModel,
} from '@mui/x-data-grid-pro';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { Box } from '@mui/material';
import { useBackend } from '@/hooks/useBackend';
import { log } from '@/lib/utils/logger';
import { dataGridStyle } from '@/styles/tableStyles';
import { useSystemConfig } from '@/hooks/useSystemConfig';
import { DataGridToolbar } from '@/components/tables/DataGridToolbar';
import { useTheoryActivityFilterStore } from '@/stores/theoryActivityFilterStore';
import { StatusDto, TheoryActivityDto } from '@/api/ApiClient';
import { STATUS_MAP } from '@/lib/types/statusTypes';
import { useUserResolution } from '@/lib/options/useUserResolution';
import { removeLeadingTrailingBrTags } from '@/lib/utils/textUtils';
import { useGridSearchErrorHandler } from '@/hooks/useGridSearchErrorHandler';
import { consumeNextSearchUserInitiated } from '@/stores/searchUiStore';

// Helper function to render "N/A" values in italics
const renderCellValue = (value: string | undefined | null) => {
  const displayValue = value || 'N/A';
  if (displayValue === 'N/A') {
    return <span style={{ fontStyle: 'italic' }}>{displayValue}</span>;
  }
  return displayValue;
};

const formatDate = (dateString: string | undefined | null) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString();
  } catch {
    return 'N/A';
  }
};

const getTechniqueName = (id: string | undefined, options: any) => {
  if (!id) return 'N/A';
  return options.synthesisTechniques?.find((entry: any) => entry.id === id)?.name || id;
};

export type TheoryActivityRowType = {
  id: number;
  description?: string | null;
  date?: string;
  technique?: string;
  techniqueName?: string;
  software?: string;
  version?: string;
  projectLabel?: string;
  dataPackageLabel?: string;
  status?: StatusDto;
  statusLabel?: string;
  createdBy?: string;
  lastChangedDate?: string;
  lastChangedBy?: string;
};

interface TheoryActivityGridCoreProps {
  /** Whether to show links */
  showLinks?: boolean;
  /** Whether to enable row selection */
  enableSelection?: boolean;
  /** Selection model for controlled selection */
  selectionModel?: GridRowSelectionModel;
  /** Callback for selection changes */
  onSelectionChange?: (selection: GridRowSelectionModel) => void;
  /** Whether to show checkboxes for multiple selection */
  checkboxSelection?: boolean;
  /** Whether to disable column menu */
  disableColumnMenu?: boolean;
  /** Whether to show the toolbar (columns, density, export) */
  showToolbar?: boolean;
  /** Grid density setting */
  density?: GridDensity;
  /** Callback when an activity is selected */
  onActivitySelect?: (activity: TheoryActivityRowType) => void;
  /** Callback when multiple activities are selected */
  onActivitiesSelect?: (activities: TheoryActivityRowType[]) => void;
  /** Options data */
  optionsData?: {
    synthesisTechniques?: any[];
    status?: any[];
  };
  /** Whether options are loading */
  optionsLoading?: boolean;
}

export const TheoryActivityGridCore: FC<TheoryActivityGridCoreProps> = ({
  showLinks = true,
  enableSelection = false,
  selectionModel,
  onSelectionChange,
  checkboxSelection = false,
  disableColumnMenu = false,
  showToolbar = true,
  density = 'standard',
  onActivitySelect,
  onActivitiesSelect,
  optionsData,
  optionsLoading = false,
}) => {
  const backend = useBackend();
  const systemConfig = useSystemConfig();
  const { resolveUser } = useUserResolution();
  const { filters, paginationModel, sortModel, setPaginationModel, setSortModel, setIsSearching } =
    useTheoryActivityFilterStore();
  const { handleGridSearchError } = useGridSearchErrorHandler('theory activities');

  const [rows, setRows] = useState<TheoryActivityRowType[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({
    id: false, // Hide ID by default
    lastChangedDate: false, // Hide by default
    lastChangedBy: false, // Hide by default
  });

  // Map theory activity DTO to row type
  const mapToRowType = useCallback(
    (activity: TheoryActivityDto): TheoryActivityRowType => {
      return {
        id: activity.id!,
        description: activity.desc || null,
        date: activity.date ? activity.date.toString() : undefined,
        technique: activity.simulationTechnique,
        techniqueName: getTechniqueName(activity.simulationTechnique, optionsData || {}),
        software: activity.software,
        version: activity.version,
        projectLabel: activity.projectLabel,
        dataPackageLabel: activity.dataPackageLabel,
        status: activity.status,
        statusLabel: activity.status ? STATUS_MAP[activity.status]?.label || activity.status : 'N/A',
        createdBy: resolveUser(activity.userId!) || activity.userId || 'N/A',
        lastChangedDate: activity.lastChangedDate ? activity.lastChangedDate.toString() : undefined,
        lastChangedBy: resolveUser(activity.lastChangedByUser!) || activity.lastChangedByUser || 'N/A',
      };
    },
    [optionsData, resolveUser]
  );

  // Fetch data when filters, pagination, or sort change
  useEffect(() => {
    if (!backend) return;

    const fetchData = async () => {
      const userInitiatedSearch = consumeNextSearchUserInitiated();
      try {
        setIsSearching(true);
        setLoading(true);
        log.debug('[TheoryActivityGridCore] Fetching with filters:', filters);

        const result = await backend.theoryActivity.search(
          sortModel[0]?.field || 'date',
          sortModel[0]?.sort === 'asc' ? 'Ascending' : 'Descending',
          paginationModel.page * paginationModel.pageSize,
          paginationModel.pageSize,
          0, // draw
          filters
        );

        log.debug('[TheoryActivityGridCore] Raw result from backend:', result.data?.[0]);

        const mappedRows = result.data?.map(mapToRowType) || [];
        setRows(mappedRows);
        setRowCount(result.recordsTotal || 0);

        log.debug('[TheoryActivityGridCore] Fetched activities:', mappedRows.length);
        log.debug('[TheoryActivityGridCore] First mapped row:', mappedRows[0]);
      } catch (error) {
        log.error('[TheoryActivityGridCore] Error fetching activities:', error);
        handleGridSearchError(error, { userInitiatedSearch });
        setRows([]);
        setRowCount(0);
      } finally {
        setLoading(false);
        setIsSearching(false);
      }
    };

    fetchData();
  }, [backend, filters, paginationModel, sortModel, mapToRowType, setIsSearching, handleGridSearchError]);

  // Handle selection
  const handleRowSelectionModelChange = useCallback(
    (newSelection: GridRowSelectionModel) => {
      onSelectionChange?.(newSelection);

      if (onActivitiesSelect) {
        const selectedRows = rows.filter((row) =>
          Array.isArray(newSelection) ? newSelection.includes(row.id) : newSelection.ids.has(row.id)
        );
        onActivitiesSelect(selectedRows);
      }
    },
    [rows, onSelectionChange, onActivitiesSelect]
  );

  const handleRowClick = useCallback(
    (params: any) => {
      if (enableSelection && onActivitySelect) {
        const row = rows.find((r) => r.id === params.row.id);
        if (row) {
          onActivitySelect(row);
        }
      }
    },
    [enableSelection, onActivitySelect, rows]
  );

  const columns = useMemo<GridColDef[]>(() => {
    const cols: GridColDef[] = [
      {
        field: 'id',
        headerName: 'ID',
        width: 80,
        renderCell: (params) => {
          if (showLinks) {
            return (
              <Link href={`${systemConfig?.basePath ?? ''}/theory-activities/${params.value}`}>
                {renderCellValue(params.value?.toString())}
              </Link>
            );
          }
          return renderCellValue(params.value?.toString());
        },
      },
      {
        field: 'date',
        headerName: 'Date',
        flex: 1,
        minWidth: 120,
        renderCell: (params) => renderCellValue(formatDate(params.value)),
      },
      {
        field: 'techniqueName',
        headerName: 'Technique',
        flex: 1,
        minWidth: 150,
        sortable: false,
        renderCell: (params) => renderCellValue(params.value),
      },
      {
        field: 'software',
        headerName: 'Software',
        flex: 1,
        minWidth: 150,
        renderCell: (params) => renderCellValue(params.value),
      },
      {
        field: 'version',
        headerName: 'Version',
        flex: 1,
        minWidth: 120,
        renderCell: (params) => renderCellValue(params.value),
      },
      {
        field: 'projectLabel',
        headerName: 'Project',
        flex: 1,
        minWidth: 150,
        sortable: false,
        renderCell: (params) => {
          if (showLinks && params.value) {
            return (
              <Link href={`${systemConfig?.basePath ?? ''}/projects/${encodeURIComponent(params.value)}`}>
                {renderCellValue(params.value)}
              </Link>
            );
          }
          return renderCellValue(params.value);
        },
      },
      {
        field: 'dataPackageLabel',
        headerName: 'Dataset',
        flex: 1,
        minWidth: 150,
        sortable: false,
        renderCell: (params) => renderCellValue(params.value),
      },
      {
        field: 'statusLabel',
        headerName: 'Status',
        flex: 1,
        minWidth: 120,
        sortable: false,
        renderCell: (params) => renderCellValue(params.value),
      },
      {
        field: 'createdBy',
        headerName: 'Created By',
        flex: 1,
        minWidth: 150,
        sortable: false,
        renderCell: (params) => renderCellValue(params.value),
      },
      {
        field: 'lastChangedDate',
        headerName: 'Last Changed',
        flex: 1,
        minWidth: 150,
        renderCell: (params) => renderCellValue(formatDate(params.value)),
      },
      {
        field: 'lastChangedBy',
        headerName: 'Changed By',
        flex: 1,
        minWidth: 150,
        sortable: false,
        renderCell: (params) => renderCellValue(params.value),
      },
    ];

    return cols;
  }, [showLinks, systemConfig]);

  return (
    <DataGridPro
      rows={rows}
      columns={columns}
      rowCount={rowCount}
      loading={loading || optionsLoading}
      pagination
      paginationMode="server"
      paginationModel={paginationModel}
      onPaginationModelChange={setPaginationModel}
      sortingMode="server"
      sortModel={sortModel}
      onSortModelChange={setSortModel}
      pageSizeOptions={[10, 25, 50, 100]}
      checkboxSelection={checkboxSelection}
      disableRowSelectionOnClick={!enableSelection}
      rowSelectionModel={selectionModel}
      onRowSelectionModelChange={handleRowSelectionModelChange}
      onRowClick={handleRowClick}
      columnVisibilityModel={columnVisibilityModel}
      onColumnVisibilityModelChange={setColumnVisibilityModel}
      disableColumnMenu={disableColumnMenu}
      density={density}
      getRowHeight={() => 'auto'}
      getDetailPanelContent={({ row }) => {
        const desc = removeLeadingTrailingBrTags(row.description || '');
        const isValidDesc = desc && desc !== '<br>' && desc !== 'N/A';

        if (!isValidDesc) {
          return null;
        }

        return (
          <Box
            sx={{
              bgcolor: '#f9f9f9',
              border: '1px solid #ccc',
              borderRadius: 2,
              boxShadow: 2,
              m: 2,
              p: 2,
            }}
          >
            <Box sx={{ fontWeight: 'bold', fontSize: '1rem', mb: 1 }}>Description:</Box>
            <Box
              sx={{
                whiteSpace: 'pre-wrap',
                fontSize: '0.95rem',
                color: '#333',
              }}
              dangerouslySetInnerHTML={{ __html: desc }}
            />
          </Box>
        );
      }}
      getDetailPanelHeight={({ row }) => {
        const desc = removeLeadingTrailingBrTags(row.description || '');
        const isValidDesc = desc && desc !== '<br>' && desc !== 'N/A';
        return isValidDesc ? 150 : 0;
      }}
      slots={{
        toolbar: showToolbar ? DataGridToolbar : undefined,
      }}
      sx={dataGridStyle}
      initialState={{
        density: density,
      }}
    />
  );
};
